import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

interface FreshStoryBreakingHeroProps {
  story: ExploreStoryCard;
  exploreBase: string;
  imageSizes?: string;
}

/**
 * Large hero card for breaking stories when ≤3 stories are present.
 * Takes ~60% width with a dominant image and generous typography.
 * Uses proportional flex (3:2 image:content) so the image grows
 * to fill available grid height — no fixed aspect-ratio.
 */
export function FreshStoryBreakingHero({
  story,
  exploreBase,
  imageSizes = "(max-width: 1023px) 60vw, 540px",
}: FreshStoryBreakingHeroProps) {
  // This component is only used for confirmed BREAKING + HIGH stories,
  // so always force the breaking badge regardless of storyBadge derivation.
  const badgeStyleClass = STORY_BADGE_STYLES.breaking;
  const shouldOptimizeImage = story.coverImageUrl
    ? canOptimizeExploreImage({ src: story.coverImageUrl })
    : false;

  return (
    <Link
      className="group hover:-translate-y-1.5 flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--explore-card-border)] bg-[var(--explore-bg-card)] no-underline shadow-[var(--explore-card-shadow)] transition-[transform,box-shadow] duration-300 ease-out hover:shadow-[var(--explore-card-shadow-hover)] active:scale-[0.98]"
      href={`${exploreBase}/stories/${story.storyId}`}
      prefetch={false}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Image — proportional flex, grows with card height */}
      <div className="relative min-h-[220px] flex-[3] overflow-hidden bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        {story.coverImageUrl && (
          <Image
            alt={story.headline}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
            fill
            sizes={imageSizes}
            src={story.coverImageUrl}
            unoptimized={!shouldOptimizeImage}
          />
        )}

        {/* NEW badge overlay */}
        {story.isNew && (
          <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-full bg-[var(--explore-badge-overlay-bg)] px-3 py-1.5 shadow-[var(--explore-card-shadow)] backdrop-blur-[10px]">
            <span className="h-2.5 w-2.5 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
            <span className="font-bold text-[0.7rem] text-[var(--explore-text-primary)] uppercase tracking-[0.03em]">
              New
            </span>
            <span className="h-3.5 w-px bg-[var(--explore-border)]" />
            <span className="font-medium text-[0.65rem] text-[var(--explore-text-muted)]">
              {story.timeAgo}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col px-5 pt-6 pb-6">
        {/* Headline — large editorial size */}
        <h3
          className="text-pretty font-semibold text-[1.4rem] text-[var(--explore-text-primary)] leading-[1.3]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        {/* Summary — generous 5-line clamp to fill space */}
        <p className="mt-2 line-clamp-5 text-[0.88rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          {story.summary}
        </p>

        {/* Badge + category + time — always at the bottom */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span
            className={`inline-flex items-center rounded-sm px-2 py-0.5 font-bold text-[0.6rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
          >
            Breaking
          </span>
          {story.categoryEmoji && (
            <span className="text-[0.8rem]">{story.categoryEmoji}</span>
          )}
          {story.categoryDisplayName && (
            <span className="font-medium text-[0.78rem] text-[var(--explore-text-secondary)]">
              {story.categoryDisplayName}
            </span>
          )}
          <span className="text-[0.72rem] text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
          <span className="text-[0.72rem] text-[var(--explore-border)]">·</span>
          <span className="text-[0.72rem] text-[var(--explore-text-muted)]">
            {story.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}
